import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const HotelType = () => {
  const [hotelTypes, setHotelTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ id: '', name: '', image: null });
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    fetchHotelTypes();
  }, []);

  const fetchHotelTypes = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('hotelTypeList');
      setHotelTypes(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch hotel types');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({ id: '', name: '', image: null });
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (hotelType) => {
    setFormData({ id: hotelType._id, name: hotelType.name, image: null });
    setImagePreview(hotelType.image ? `${mediaUrl}${hotelType.image}` : '');
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Please choose a smaller image less than 2MB');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      if (formData.id) data.append('id', formData.id);
      data.append('name', formData.name);
      if (formData.image) data.append('image', formData.image);

      await apiService.postFormData('updateHotelType', data);
      toast.success('Hotel Category updated successfully');
      setShowModal(false);
      fetchHotelTypes();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      await apiService.get(`hotelTypeStatus/${id}`);
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const handleTopToggle = async (id) => {
    try {
      await apiService.get(`hotelTypeTopStatus/${id}`);
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  return (
    <div className="flex-1 p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Hotel Category</h3>
          <button
            onClick={handleAddNew}
            className="flex items-center px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#009ef7' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0084d4'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#009ef7'}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
            </div>
          ) : hotelTypes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hotel types found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hotelTypes.map((hotelType) => (
                    <tr key={hotelType._id}>
                      <td className="px-2 sm:px-4 py-4">
                        {hotelType.image && (
                          <img
                            src={`${mediaUrl}${hotelType.image}`}
                            alt={hotelType.name}
                            className="h-24 w-36 object-cover rounded-lg"
                          />
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">{hotelType.name}</td>
                      <td className="px-2 sm:px-4 py-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hotelType.top === 1}
                            onChange={() => handleTopToggle(hotelType._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-2 sm:px-4 py-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hotelType.status === 1}
                            onChange={() => handleStatusToggle(hotelType._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-2 sm:px-4 py-4">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleEdit(hotelType)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                          >
                            <i className="bi bi-pencil text-lg"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Update Hotel Category</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter Hotel Category Name"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image <span className="text-red-500">*</span>
                  </label>
                  <div className="relative inline-block">
                    <div
                      className="w-72 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                      style={{
                        backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      {!imagePreview && <i className="bi bi-image text-4xl text-gray-400"></i>}
                    </div>
                    <label
                      className="absolute bottom-2 right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                      title="Change image"
                    >
                      <i className="bi bi-pencil text-xs"></i>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview('');
                          setFormData({ ...formData, image: null });
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center text-red-600 hover:bg-red-50"
                        title="Remove image"
                      >
                        <i className="bi bi-x text-lg"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                    style={{ backgroundColor: '#009ef7' }}
                  >
                    {submitting ? 'Please wait...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelType;
